import { Button } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { generateFloodReport } from '~/api/projects2';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Notify from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import type { ProjectDetailsParams } from '..';
import projectDetailsStore from '../store';
import LayerSection from './LayerSection';
import SWPPPTool from './SWPPPTool';

export function MapChildren() {
  return null;
}

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { isProjectInCalifornia } = useSnapshot(projectDetailsStore);
  const [_featureSize, setFeatureSize] = useState({
    exclusionLandHeldInConservationEasementCCED: -1,
    exclusionProtectedAreaDatabaseHoldingsCPAD: -1,
    exclusionUSGSProtectedAreas: -1,
    environmentalWetlands: -1,
    environmentalTerrestrialSpecies: -1,
    environmentalAvian: -1,
    environmentalUSFWSCriticalHabitat: -1,
  });

  function _setFeatureSize(layerId: string, size: number) {
    setFeatureSize((featureSize) => ({
      ...featureSize,
      [layerId]: size,
    }));
  }

  const [floodMapConfirmOpen, setFloodMapConfirmOpen] = useState(false);

  const handleGenerateFloodReport = async (ccEmails: string[] | undefined) => {
    try {
      await generateFloodReport(projectId, ccEmails);
      notifyExportQueued({
        message: 'We are generating your FEMA Flood Map. You will receive an email with the flood map shortly.',
        ccEmails,
      });
      captureTypedPosthogEvent(POSTHOG_EVENTS.femaFloodReportGenerated, {
        project_id: projectId,
      });
    } catch (e) {
      const reason = isAxiosError(e) ? (e?.response?.data?.reason ?? e?.response?.status) : undefined;
      console.error(e);
      captureTypedPosthogEvent(POSTHOG_EVENTS.femaFloodReportGenerationFailed, {
        project_id: projectId,
        reason: reason ? String(reason) : 'api_error',
      });
      Notify.error({
        message:
          'There was an error generating your FEMA Flood Map. Please try again later. If issues persist, please contact support@civilgrid.com.',
      });
      return false;
    }
  };

  return (
    <div className="w-full pb-24">
      <div className="flex flex-col space-y-2 p-4">
        <div className="text-base font-medium">FEMA Flood Map</div>
        <div className="text-sm">Generate FEMA Flood Map(s) for your project site.</div>
        <div className="text-xs italic">
          Disclaimer: This flood map is obtained from FEMA; CivilGrid is not responsible for its accuracy.
        </div>
        <div className="w-1/2">
          <Button onClick={() => setFloodMapConfirmOpen(true)} data-testid="environmental-fema-flood-map-button">
            Request FEMA Flood Map
          </Button>
        </div>
        <SendEmailModal
          open={floodMapConfirmOpen}
          onOpenChange={setFloodMapConfirmOpen}
          title="Request FEMA Flood Map"
          testIdPrefix="environmental-fema-flood-map"
          onSend={(ccEmails) => handleGenerateFloodReport(ccEmails.length > 0 ? ccEmails : undefined)}
        />
      </div>
      {isProjectInCalifornia && (
        <LayerSection
          title="Watershed"
          layerId={'environmentalWatersheds'}
          fields={['RBUASPW', 'RBNAME', 'HUNAME', 'HANAME']}
          description={
            <div className="flex flex-col gap-2">
              <div className="text-sm">
                Watershed definitions based on the California Interagency Watershed Map of 1999
              </div>
              <SWPPPTool />
            </div>
          }
        />
      )}

      {isProjectInCalifornia && (
        <LayerSection
          title="Restricted Lands - Conservation Easement"
          layerId={'exclusionLandHeldInConservationEasementCCED'}
          fields={['sitename', 'eholdtyp', 'esmthldr']}
          description={
            'Your Project site contains a restricted land use; therefore, development may be prohibited/limited in this area. CivilGrid recommends confirming this land use restriction with the owner/agency.'
          }
          setFeatureSize={_setFeatureSize}
        />
      )}
      {isProjectInCalifornia && (
        <LayerSection
          title="Restricted Lands - The California Protected Areas Database (CPAD)"
          layerId={'exclusionProtectedAreaDatabaseHoldingsCPAD'}
          fields={['UNIT_NAME', 'ACCESS_TYP', 'AGNCY_NAME']}
          description={
            'Your Project site contains a restricted land use; therefore, development may be prohibited/limited in this area. CivilGrid recommends confirming this land use restriction with the owner/agency.'
          }
          setFeatureSize={_setFeatureSize}
        />
      )}
      <LayerSection
        title="Restricted Lands - USGS Protected Areas"
        layerId={'exclusionUSGSProtectedAreas'}
        fields={['Loc_Nm', 'Loc_Ds', 'Loc_Mang']}
        description={
          'Your Project site contains a restricted land use; therefore, development may be prohibited/limited in this area. CivilGrid recommends confirming this land use restriction with the owner/agency.'
        }
        setFeatureSize={_setFeatureSize}
      />
      {isProjectInCalifornia && (
        <LayerSection
          title="Wetlands"
          layerId={'environmentalWetlands'}
          fields={['orig_class', 'organization', 'leglabellevel1', 'leglabellvel2', 'clicklabel', 'lastupdate']}
          description={
            <div>
              <p>
                It is recommended that an environmental consultant perform a site visit and prepare a biological
                resources technical report to confirm the results of this analysis. Should a wetland be present onsite,
                CivilGrid recommends performing a wetland delineation to outline its jurisdictional boundaries. Should
                the wetland be located within the footprint of your project, you may need to obtain a permit from USACE.
                If the delineation reveals the project will not be located within the boundaries of the wetland, no
                permit is anticipated to be required.
              </p>
              <p>
                Should the project include activities within the bed, channel, or bank of any river, stream or lake
                (including riparian vegetation) a permit may be required from CDFW.
              </p>
            </div>
          }
          setFeatureSize={_setFeatureSize}
        />
      )}
      {isProjectInCalifornia && (
        <LayerSection
          title="Threatened or Endangered Terrestrial Species"
          layerId={'environmentalTerrestrialSpecies'}
          fields={['CNAME', 'SENSITIVE', 'CALLIST', 'FEDLIST']}
          description={
            'If the site is un-developed or contains native vegetation, CivilGrid recommends an overall site review as well as species-specific surveys be conducted by a biologist to confirm species presence. Results of these surveys will be used to determine whether or not your project will trigger a permit(s). Should your project trigger a permit from CDFW and/or USFWS, approval timelines and fees will be dependent on the level of project impact.'
          }
          setFeatureSize={_setFeatureSize}
        />
      )}
      {isProjectInCalifornia && (
        <LayerSection
          title="Avian Nests within 0.5 Miles"
          layerId={'environmentalAvian'}
          fields={['CNAME', 'CDFWSTATUS', 'CALLIST', 'FEDLIST']}
          description={
            'There are previous records of nesting birds within 0.5 miles of the project area. Due to the potential presence of avian species, it is recommended that a pre-construction nesting bird survey be conducted during nesting bird season by a biologist. Should results from the survey identify an active nest within your project area, the biologist may recommend mitigation measures specific to your project.'
          }
          setFeatureSize={_setFeatureSize}
        />
      )}
      <LayerSection
        title="Critical Habitat"
        layerId={'environmentalUSFWSCriticalHabitat'}
        fields={['comname', 'listing_st']}
        description={
          'Critical habitat may be present within the project area. CivilGrid recommends an overall site review be conducted by a biologist to confirm presence of critical habitat. Results of this survey will be used to determine whether or not your project will trigger a permit from the USFWS.'
        }
        setFeatureSize={_setFeatureSize}
      />
    </div>
  );
}
